import {
  Activity,
  Bot,
  Cloud,
  Code2,
  Cpu,
  CreditCard,
  Database,
  Hammer,
  Eye,
  Globe,
  Inbox,
  KeyRound,
  Layers,
  MessageSquare,
  MonitorCog,
  Network,
  ScrollText,
  Server,
  ShieldCheck,
  Terminal,
  Workflow,
  Library,
  Box,
  Clapperboard,
} from 'lucide-react';

export interface PlatformRoute {
  path: string;
  label: string;
  eyebrow: string;
  title: string;
  description: string;
  icon: typeof Cloud;
  primaryAction?: string;
  /** In-app surface the primary CTA opens. See platformCtaHref. */
  appHref?: string;
  command?: string;
  cards: Array<{
    title: string;
    body: string;
  }>;
}

export const platformRoutes: PlatformRoute[] = [
  {
    path: '/chat',
    label: 'Chat',
    eyebrow: 'chat and research',
    title: 'AI chat with live web search and deep research.',
    description: 'Ask questions, attach files, search the live web, run multi-step research, and query papers. Model choice, citations, and history stay in the same workspace.',
    icon: MessageSquare,
    primaryAction: 'Start chatting',
    appHref: '/assistants',
    command: 'app chat "research R2-backed model hosting" --web --deep --papers',
    cards: [
      { title: 'Everyday chat', body: 'Answers from the model you pick, with saved history and file or image context.' },
      { title: 'Web search', body: 'Cited answers from the live web. Pick the provider and how fresh results should be.' },
      { title: 'Deep research', body: 'A run that reads the sources and returns a cited report.' },
    ],
  },
  {
    path: '/projects',
    label: 'Projects',
    eyebrow: 'workspace',
    title: 'One project for repos, agents, deploys, and keys.',
    description: 'Group apps, repos, coding agents, domains, and API keys in one project. Billing, deploy history, and agent runs stay with it.',
    icon: Layers,
    primaryAction: 'Create a project',
    appHref: '/apps',
    command: 'app api GET /api/me',
    cards: [
      { title: 'Start in minutes', body: 'Begin with one app. Add teammates, roles, and environments when you need them.' },
      { title: 'Grows with you', body: 'Start with one app and scale to production without switching tools.' },
      { title: 'Same everywhere', body: 'The CLI and the web app do the same things, so scripts and clicks match.' },
    ],
  },
  {
    path: '/auth',
    label: 'Auth',
    eyebrow: 'app sign-in',
    title: 'Add sign-in without running an auth service.',
    description: 'Register a browser, mobile, or server app, and app.nz handles accounts, consent, sessions, and profiles.',
    icon: ShieldCheck,
    primaryAction: 'Register an app',
    appHref: '/developers',
    command: "import { createAuthClient } from 'https://app.nz/appnz-auth.js'",
    cards: [
      { title: 'Drop-in client', body: 'A small hosted JavaScript client handles tokens, sessions, and auth state.' },
      { title: 'Safe public clients', body: 'Public apps use Authorization Code + PKCE. No secrets in browsers.' },
      { title: 'Scoped by consent', body: 'Each app gets only the profile, credit, or key scopes the user approved. Redirect URIs must match exactly.' },
    ],
  },
  {
    path: '/sites',
    label: 'Sites',
    eyebrow: 'hosted pages',
    title: 'Build and publish a site right in your browser.',
    description: 'Create a page, edit HTML, CSS, and JS in the builder, and publish to app.nz/sites/<slug>/. No build step, no DNS wait.',
    icon: Globe,
    primaryAction: 'Open the builder',
    appHref: '/sites',
    command: 'app sites create my-landing-page --title "My landing page"',
    cards: [
      { title: 'Live in one click', body: 'Your site goes live when you save.' },
      { title: 'Edit in the browser', body: 'A multi-file editor with live preview and nothing to install.' },
      { title: 'Let an agent help', body: 'Hand a page to a coding agent, then review the published result.' },
    ],
  },
  {
    path: '/deploys',
    label: 'Deploys',
    eyebrow: 'app hosting',
    title: 'Deploy AI apps, APIs, workers, and preview branches.',
    description: 'Ship containers to managed CPU and GPU. Scale into inference, custom build runners, and model endpoints as the product grows.',
    icon: Cloud,
    primaryAction: 'Deploy an app',
    // Deploys are driven by `app apps deploy`; go straight to setup instead of
    // bouncing signed-in users through /signup and back to this marketing page.
    appHref: '/docs/cli',
    command: 'app apps deploy ./my-ai-app',
    cards: [
      { title: 'Your own subdomain', body: 'Claim a name like my-ai-app.app.nz, point it at a deploy, and get HTTPS automatically.' },
      { title: 'Safe by design', body: 'Secrets stay protected and agent commands run sandboxed.' },
      { title: 'Easy rollback', body: 'Every deploy keeps its commit, image, logs, and last working version.' },
    ],
  },
  {
    path: '/cogs',
    label: 'Cogs',
    eyebrow: 'GPU model hosting',
    title: 'Run any Cog container as a scale-to-zero GPU endpoint.',
    description: 'Deploy a Cog or ML container as an app.nz endpoint. It scales to zero at published prices, queues requests, and serves a generated web UI.',
    icon: Cpu,
    primaryAction: 'Deploy a cog',
    appHref: '/cogs',
    command: 'app api POST /api/cogs \'{"name":"fast-vfx","image":"r8.im/lee101/fast-vfx","hardware":"gpu-rtx4090"}\'',
    cards: [
      { title: 'Bring any cog', body: 'Point at a Cog image and run it unchanged. Inputs and outputs come through as-is — video, image, audio, or JSON.' },
      { title: 'Scale to zero', body: 'An idle model costs nothing. The first prediction warms a GPU pod; it sleeps again after a quiet window.' },
      { title: 'Queue API included', body: 'Every model gets a queue API with job status and position, plus the generated UI.' },
    ],
  },
  {
    path: '/comfy',
    label: 'Comfy',
    eyebrow: 'ComfyUI hosting',
    title: 'Deploy ComfyUI workflows as scale-to-zero GPU endpoints.',
    description: 'Paste a ComfyUI workflow, expose the inputs callers need, and serve it as an endpoint that scales from zero.',
    icon: Cpu,
    primaryAction: 'Create a workflow endpoint',
    appHref: '/comfy',
    command: 'app api POST /api/comfy \'{"name":"sdxl-workflow","hardware":"auto","mode":"auto"}\'',
    cards: [
      { title: 'Bring any workflow', body: 'Save your workflow in API format and expose only the inputs callers need.' },
      { title: 'Serverless to pod', body: 'Light traffic runs serverless. Sustained traffic gets a warm pod, then back to zero when idle.' },
      { title: 'Model cache included', body: 'Model files are cached once and reused across deployments. Pin heavy workflows to dedicated hardware when needed.' },
    ],
  },
  {
    path: '/spaces/splat',
    label: 'Splat',
    eyebrow: '3D capture',
    title: 'Photos in, gaussian splat out — with mesh export.',
    description: 'Upload 2-64 photos and get a splat you can orbit in the browser, plus point cloud and textured mesh downloads. Pay per capture, with no subscription.',
    icon: Box,
    primaryAction: 'Create a capture',
    appHref: '/spaces/splat',
    command: 'app api POST /api/splat/jobs \'{"imageUrls":["https://…/1.jpg","https://…/2.jpg"],"tier":"quality"}\'',
    cards: [
      { title: 'Pose-free pipeline', body: 'No camera setup. Photos in, splat out.' },
      { title: 'View and export', body: 'Orbit it in the browser, then download .splat, point cloud, or textured GLB mesh.' },
      { title: 'Per-capture pricing', body: 'Fast $0.25, Quality with mesh $0.75. Competitors charge $13-30/mo subscriptions for the same exports.' },
    ],
  },
  {
    path: '/spaces/fast-video',
    label: 'Fast Video',
    eyebrow: 'video generation',
    title: 'LTX-2.3 video generation at a fraction of API prices.',
    description: 'LTX-2.3 turns text or images into video with native audio, up to 4K and 20 seconds, billed per second of output. 1080p costs $0.025/s; elsewhere it costs $0.04-0.06.',
    icon: Clapperboard,
    primaryAction: 'Generate a video',
    appHref: '/spaces/fast-video',
    command: 'app api POST /api/fastvideo/generations \'{"prompt":"waves at dusk","resolution":"1080p","seconds":5}\'',
    cards: [
      { title: 'Built for speed', body: 'An optimized LTX-2.3 pipeline keeps the price per second low.' },
      { title: 'Audio + 4K', body: 'Native synced audio plus upscaling to 1440p and 4K.' },
      { title: 'Per-second billing', body: 'Pay only for output seconds. Idle GPUs cost nothing.' },
    ],
  },
  {
    path: '/builds',
    label: 'Builds',
    eyebrow: 'container CI + registry',
    title: 'Build containers on our cloud, push to your private registry.',
    description: 'Build cog and app images on our autoscaling builder fleet and push them to your private registry. Nothing is public.',
    icon: Hammer,
    primaryAction: 'Start a build',
    appHref: '/builds',
    command: 'app api POST /api/builds \'{"image":"my-cog","tag":"latest","dockerfile":"FROM scratch"}\'',
    cards: [
      { title: 'Your own registry', body: 'Images push to a private per-user namespace. Pods pull them with a credential you store once.' },
      { title: 'Autoscaling builders', body: 'The builder fleet scales up with the queue and to zero when idle, balanced across boxes.' },
      { title: 'Straight into inference', body: 'A pushed image drops into Cog Studio and runs on a scale-to-zero GPU.' },
    ],
  },
  {
    path: '/agents',
    label: 'Agents',
    eyebrow: 'cloud coding agents',
    title: 'Autonomous coding agents in the cloud.',
    description: 'Point an agent at a repo and describe the work in plain English. It branches, codes, tests, and opens a pull request, then runs until you approve or stop it.',
    icon: Bot,
    primaryAction: 'Run an agent',
    appHref: '/agent',
    command: 'app agent run --prompt "fix the failed deploy" --repo acme/api --model openpaths/auto-code',
    cards: [
      { title: 'You set the guardrails', body: 'Pick model, machine size, branch, skills, spend cap, and timeout, and choose whether PRs merge automatically.' },
      { title: 'Visual proof before merge', body: 'VisualBench captures screenshots, lets the agent self-correct the UI, and only asks you when it looks right.' },
      { title: 'Audit every run', body: 'Logs, diffs, screenshots, and cost for each task, so you know exactly what shipped.' },
    ],
  },
  {
    path: '/skills',
    label: 'Skills',
    eyebrow: 'searchable skill library',
    title: 'Search a library of ready-to-run agent skills.',
    description: 'Hundreds of self-contained agent skills, each with its setup included. Author your own, or copy one and hand it to any agent.',
    icon: Library,
    primaryAction: 'Search skills',
    appHref: '/skills',
    command: 'app skills search "code review"',
    cards: [
      { title: 'Semantic search', body: 'Skills rank by meaning rather than keyword matches.' },
      { title: 'Self-contained', body: 'Every skill ships its own setup steps, so it runs standalone.' },
      { title: 'API + CLI', body: 'Search from the CLI, or call the API from anything.' },
    ],
  },
  {
    path: '/gateway',
    label: 'Gateway',
    eyebrow: 'model gateway',
    title: 'One OpenAI-compatible API for every model and provider.',
    description: 'Route chat, embeddings, images, audio, web search, and paper search through app.nz. Model choice, failover, metering, and hosted endpoints sit behind one API key.',
    icon: Network,
    primaryAction: 'Create a key',
    appHref: '/api-keys',
    command: 'app gateway models',
    cards: [
      { title: 'One simple API', body: 'OpenAI-compatible chat, embeddings, images, vision, and search. Switch providers without rewriting code.' },
      { title: 'Smart routing', body: 'Send each request to the right model: auto, code, cheap, fast, reasoning, deep search, image, or vision.' },
      { title: 'Clear costs', body: 'See spend by project, key, app, and agent run.' },
    ],
  },
  {
    path: '/pricing',
    label: 'Pricing',
    eyebrow: 'usage-based credits',
    title: 'Usage-based pricing. Prepay credits, pay for what you use.',
    description: 'One prepaid balance covers coding agents, model routing, GPU hosting, compute, and search. Pay by token or second, with optional plans for bundled credits and guaranteed machines.',
    icon: CreditCard,
    primaryAction: 'Manage credits',
    appHref: '/account',
    command: 'app credits buy --packs 25',
    cards: [
      { title: 'One balance, everything', body: 'Agents, models, GPU hosting, compute, and search all draw from the same prepaid credits. 1 credit = $0.001.' },
      { title: 'Metered, with caps', body: 'Pay per token and per second. Set spend caps per project, key, app, or agent run so there are no surprises.' },
      { title: 'Plans are optional', body: 'Pro, Ultra & Max bundle monthly credits at a better rate plus guaranteed machines. Usage beyond them continues at list price.' },
    ],
  },
  {
    path: '/workers',
    label: 'Workers',
    eyebrow: 'compute',
    title: 'CPU and GPU workers for builds, agents, and inference.',
    description: 'Join machines to the app.nz cloud for app builds, agent sandboxes, hosted model inference, and batch jobs, with heartbeats, caps, and live logs.',
    icon: Server,
    primaryAction: 'Add a worker',
    appHref: '/cli',
    command: 'app api GET /api/platform/status?test=true',
    cards: [
      { title: 'CPU workers', body: 'Build apps, run services and tests, and host previews.' },
      { title: 'GPU workers', body: 'Run hosted models, generate images and video, and run batch inference.' },
      { title: 'Always accountable', body: 'Workers report in, pick up jobs, stream logs, and respect your caps.' },
    ],
  },
  {
    path: '/queues',
    label: 'Queues',
    eyebrow: 'queues',
    title: 'Task queues that feed agents and workers.',
    description: 'Push JSON jobs from your app or CLI. Workers and cloud coding agents lease, process, retry, and acknowledge messages, or turn each message into an agent task.',
    icon: Inbox,
    primaryAction: 'Create a queue',
    appHref: '/docs',
    command: 'app api POST /api/queues \'{"name":"orders","dispatch":"agent"}\'',
    cards: [
      { title: 'SQS-style by default', body: 'Visibility timeouts, retries, dead-letter, and delivery delays, without running your own broker.' },
      { title: 'Producers and consumers', body: 'Send messages over the API or CLI; consumers anywhere lease, ack, and retry with an API key.' },
      { title: 'Drive agents directly', body: 'Point a queue at agents and every message turns into a coding-agent task automatically.' },
    ],
  },
  {
    path: '/domains',
    label: 'Domains',
    eyebrow: 'domains',
    title: 'Every app gets an app.nz subdomain. Add your own later.',
    description: 'Claim a free subdomain, point it at a deploy, and get preview URLs for branches and agent tasks.',
    icon: Globe,
    primaryAction: 'Claim a name',
    appHref: '/apps',
    command: 'app sites create my-ai-app --title "My AI App"',
    cards: [
      { title: 'Claim in seconds', body: 'Pick an available name and we set up the DNS and HTTPS for you.' },
      { title: 'Preview links', body: 'Branch and task deploys get their own URLs, like pr-42-my-ai-app.app.nz, that expire on their own.' },
      { title: 'Bring your domain', body: 'Connect a custom domain with guided verification and automatic certificates.' },
    ],
  },
  {
    path: '/secrets',
    label: 'Secrets',
    eyebrow: 'keys & secrets',
    title: 'Keep your keys and secrets safe.',
    description: 'Store API keys and secrets once. Each app, deploy, and agent sees only the ones it needs.',
    icon: KeyRound,
    primaryAction: 'Set a secret',
    appHref: '/api-keys',
    command: 'app keys create --name deploy-bot',
    cards: [
      { title: 'Scoped access', body: 'Limit each secret to a project, environment, app, deploy, or agent task.' },
      { title: 'Full audit trail', body: 'See who changed what, when a secret was used, and when it was last rotated.' },
      { title: 'Encrypted, always', body: 'Values are encrypted at rest, and ready to move to your own vault whenever you want.' },
    ],
  },
  {
    path: '/logs',
    label: 'Logs',
    eyebrow: 'logs',
    title: 'One place to watch deploys, apps, agents, and model calls.',
    description: 'Every part of your project leaves a trail you can search from the web app, the CLI, or an agent.',
    icon: ScrollText,
    primaryAction: 'Tail your logs',
    appHref: '/agent',
    command: 'app agent list',
    cards: [
      { title: 'Everything in one stream', body: 'See the event, source, severity, time, and links to artifacts.' },
      { title: 'Agents can read it too', body: 'Agents pull deploy logs, failures, screenshots, and CI output through one API.' },
      { title: 'Faster answers', body: 'A clear record makes problems quick to find and quick to fix.' },
    ],
  },
  {
    path: '/billing',
    label: 'Billing',
    eyebrow: 'credits',
    title: 'One balance for hosting, agents, and model usage.',
    description: 'Credits cover deploys, workers, gateway calls, GPU jobs, and agent tasks, with shareable usage reports.',
    icon: CreditCard,
    primaryAction: 'Manage credits',
    appHref: '/account',
    command: 'app billing usage',
    cards: [
      { title: 'Set your limits', body: 'Cap spend per project, app, agent task, or hosted model.' },
      { title: 'Easy to reconcile', body: 'Every charge traced line by line.' },
      { title: 'Add expert help', body: 'Bring on managed onboarding, migrations, or custom agents when you need them.' },
    ],
  },
  {
    path: '/docs',
    label: 'Docs',
    eyebrow: 'docs',
    title: 'One key. Agents, models, deploys.',
    description: 'Launch coding agents on your repos, route chat across major models, and publish sites, with examples that match live endpoints.',
    icon: Code2,
    primaryAction: 'Read the docs',
    appHref: '/docs',
    command: 'app --help',
    cards: [
      { title: 'Organized by task', body: 'Find docs by what you are trying to do, not by how it is built.' },
      { title: 'Start simple', body: 'Begin with the basics and go deeper only when you need to.' },
      { title: 'Always current', body: 'Docs track the API, so what you read matches what you run.' },
    ],
  },
  {
    path: '/cli',
    label: 'CLI',
    eyebrow: 'terminal',
    title: 'CLI for agents, sites, keys, queues, and the model gateway.',
    description: 'Install one binary to run coding agents, publish sites, rotate keys, inspect queues, call the gateway, and reach any endpoint with app api. The same workflows as the web app, scriptable from a terminal.',
    icon: Terminal,
    primaryAction: 'Install the CLI',
    appHref: '/cli',
    command: 'curl -fsSL https://app.nz/cli.sh | sh',
    cards: [
      { title: 'Run the real surface', body: 'login, plans, credits, RA1, papers, search, gateway models, agents, queues, sites, and keys.' },
      { title: 'Drive agents', body: 'Launch a coding agent with a prompt, repo, and model, then list tasks from scripts or terminals.' },
      { title: 'Use the escape hatch', body: 'app api METHOD /path JSON reaches platform endpoints before they get a dedicated command.' },
    ],
  },
  {
    path: '/consulting',
    label: 'Consulting',
    eyebrow: 'expert help',
    title: 'Expert help when you want to move faster.',
    description: 'Hands-on help shipping faster. Every engagement leaves reusable agents, workflows, or infrastructure.',
    icon: MonitorCog,
    primaryAction: 'Start a buildout',
    appHref: '/docs/cli',
    command: 'app agent run --prompt "turn this repo into an app.nz deploy" --model openpaths/auto-code',
    cards: [
      { title: 'Migrations', body: 'Move your AI apps onto app.nz hosting, keys, deploys, and monitoring.' },
      { title: 'Custom agents', body: 'Build agents tuned to your code, data, support, or operations.' },
      { title: 'Private infrastructure', body: 'Run dedicated CPU and GPU machines for your team.' },
    ],
  },
  {
    path: '/schema',
    label: 'Reliability',
    eyebrow: 'reliability',
    title: 'Built on a solid, tested foundation.',
    description: 'Upgrades are tested before they ship, one typed model keeps behavior consistent, and capacity scales with usage.',
    icon: Database,
    primaryAction: 'See how it works',
    appHref: '/docs',
    command: 'curl https://app.nz/healthz',
    cards: [
      { title: 'Tested by default', body: 'Upgrades are tested ahead of time, so changes do not surprise you.' },
      { title: 'Consistent behavior', body: 'One typed model keeps the product predictable everywhere.' },
      { title: 'Ready to scale', body: 'Designed to grow from small projects to heavy production use.' },
    ],
  },
  {
    path: '/security',
    label: 'Security',
    eyebrow: 'security',
    title: 'Your code, agents, and secrets stay separated.',
    description: 'app.nz keeps app code, agent commands, and secrets in separate zones, so a problem in one does not reach the others.',
    icon: ShieldCheck,
    primaryAction: 'See the model',
    appHref: '/docs',
    command: 'app keys rotate',
    cards: [
      { title: 'Secrets stay protected', body: 'Accounts, billing, and secrets are handled in one trusted place.' },
      { title: 'Work runs with limits', body: 'Builds, deploys, and inference run with only the access they need.' },
      { title: 'Untrusted code is sandboxed', body: 'Agent and app commands run with tight limits on network, files, and time.' },
    ],
  },
  {
    path: '/visualbench',
    label: 'VisualBench',
    eyebrow: 'visual review',
    title: 'Visual proof for agent UI changes.',
    description: 'VisualBench captures before-and-after screenshots, compares them in the agent run, and lets coding agents self-correct the interface before you approve a merge.',
    icon: Eye,
    primaryAction: 'View latest run',
    appHref: '/visualbench',
    command: 'bun run visualbench -- http://127.0.0.1:3000/',
    cards: [
      { title: 'Before and after', body: 'Screenshots, diffs, and the exact page each one came from.' },
      { title: 'Self-correcting', body: 'The agent reviews its own screenshots, fixes the code, and tries again.' },
      { title: 'Approve with confidence', body: 'Make a passing visual check a required step before any deploy.' },
    ],
  },
  {
    path: '/status',
    label: 'Status',
    eyebrow: 'status',
    title: 'Know that app.nz is up.',
    description: 'Check the health of app.nz, the gateway, workers, queues, deploys, and model routing.',
    icon: Activity,
    primaryAction: 'Check health',
    appHref: '/docs',
    command: 'curl https://app.nz/healthz',
    cards: [
      { title: 'Core health', body: 'API, database, queues, and sign-in status.' },
      { title: 'Gateway health', body: 'Provider availability, fallbacks, latency, and errors.' },
      { title: 'Worker health', body: 'Active machines, capacity, GPU availability, and regions.' },
    ],
  },
  {
    path: '/workflows',
    label: 'Workflows',
    eyebrow: 'automation',
    title: 'Turn repeated work into one-click workflows.',
    description: 'Save steps you repeat: migrate a repo, add billing, ship a UI change, fix a deploy, or run VisualBench.',
    icon: Workflow,
    primaryAction: 'Run a workflow',
    appHref: '/agent',
    command: 'app agent run --prompt "run the migrate-to-appnz workflow" --repo acme/api --model openpaths/auto-code',
    cards: [
      { title: 'Reusable recipes', body: 'Save your steps once and rerun them whenever you need.' },
      { title: 'Built-in approvals', body: 'Require logs, screenshots, a cost summary, and sign-off before anything ships.' },
      { title: 'Share the work', body: 'Turn expert workflows into templates your whole team can use.' },
    ],
  },
];

export const platformRouteByPath = Object.fromEntries(
  platformRoutes.map((route) => [route.path, route]),
) as Record<string, PlatformRoute>;

// Destination for a route with no appHref of its own. Never the route's own
// path: a CTA pointing back at its own page is what created the signup bounce
// (marketing page -> /signup -> already authenticated -> same marketing page).
const CTA_FALLBACK_HREF = '/docs/cli';

export function platformCtaHref(route: PlatformRoute, authenticated: boolean): string {
  const destination = route.appHref || CTA_FALLBACK_HREF;
  if (authenticated) return destination;
  return `/signup?next=${encodeURIComponent(destination)}`;
}
